"use client"
import { useState, useEffect } from "react"
import { motion } from "motion/react"
import { ScrollReveal } from "@/components/scroll/ScrollReveal"
import Link from "next/link"
import { Footer } from "@/components/layout/Footer"
import { HeroRings, DotGrid, FloatingDots, ScanLine, CornerAccents } from "@/components/ui/BackgroundDecor"
import type { Currency, OffersContent } from "./content"

const BG = "#0B0F1C"
const ACCENT = "#C4541A"
const MUTED = "rgba(255,255,255,0.6)"
const SURFACE = "rgba(255,255,255,0.05)"
const BORDER = "rgba(255,255,255,0.08)"

const CURRENCIES: Record<Currency, { symbol: string; label: string; flag: string; region: string }> = {
  GBP: { symbol: "£", label: "GBP", flag: "🇬🇧", region: "United Kingdom" },
  ZAR: { symbol: "R", label: "ZAR", flag: "🇿🇦", region: "South Africa" },
}

function Eyebrow({ label }: { label: string }) {
  return (
    <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 32 }}>
      <motion.span
        initial={{ width: 0 }}
        whileInView={{ width: 32 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: [0.33, 1, 0.68, 1] }}
        style={{ height: 1, background: ACCENT, flexShrink: 0, display: "block" }}
      />
      <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: MUTED }}>{label}</span>
    </div>
  )
}

function fmtPrice(currency: Currency, amount: number) {
  return `${CURRENCIES[currency].symbol}${amount.toLocaleString("en-US")}`
}

export function OffersClient({ initialCurrency, content }: { initialCurrency: Currency; content: OffersContent }) {
  const [currency, setCurrency] = useState<Currency>(initialCurrency)
  const [billing, setBilling] = useState<"monthly" | "annual">("monthly")
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  // Tag every CTA with the variant so conversions can be attributed to A vs B.
  const contactHref = `/contact?variant=${content.variant}`

  // The opening special (if any) for the active currency — drives the top banner + the card highlight.
  const special = content.packages.find((p) => p.openingSpecial && p.openingSpecial.currency === currency)?.openingSpecial

  // Honour a saved manual choice over the geo default.
  useEffect(() => {
    try {
      const saved = localStorage.getItem("bp_currency")
      if (saved && saved in CURRENCIES) setCurrency(saved as Currency)
    } catch { /* localStorage blocked — geo default stands */ }
  }, [])

  function chooseCurrency(c: Currency) {
    setCurrency(c)
    try { localStorage.setItem("bp_currency", c) } catch { /* ignore */ }
  }

  return (
    <main style={{ background: BG, color: "#fff", minHeight: "100vh" }}>
      {special && (
        // 80px top spacer clears the fixed site header so the banner sits below the nav, not behind it.
        <div style={{ paddingTop: 80 }}>
          <div style={{ background: ACCENT, color: "#fff", textAlign: "center", padding: "11px 20px", fontSize: 14, fontWeight: 700, lineHeight: 1.45 }}>
            ★ {special.label} — {special.badge}: {fmtPrice(currency, special.upfront)} once-off + {fmtPrice(currency, special.monthly)}/mo for {special.months} months on your Website.
          </div>
        </div>
      )}
      {/* HERO */}
      <div style={{ position: "relative", overflow: "hidden" }}>
        <motion.div
          animate={{ opacity: [0.7, 1, 0.7], scale: [1, 1.08, 1] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          style={{ position: "absolute", inset: 0, pointerEvents: "none", background: `radial-gradient(ellipse 80% 65% at 85% 25%, rgba(196,84,26,0.40) 0%, transparent 66%)` }}
        />
        <HeroRings side="right" />
        <DotGrid />
        <FloatingDots />
        <ScanLine />
        <CornerAccents />
        <section style={{ padding: "clamp(100px, 18vw, 160px) clamp(20px, 4vw, 48px) 40px", maxWidth: 1400, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <ScrollReveal effect="fade-up">
            <Eyebrow label={content.eyebrow} />
            <h1 style={{ fontSize: "clamp(48px, 8vw, 120px)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 0.88, marginBottom: 40 }}>
              {content.heroTitle[0]}<br /><span style={{ color: ACCENT }}>{content.heroTitle[1]}</span>
            </h1>
            <p style={{ fontSize: "clamp(16px, 1.4vw, 20px)", color: MUTED, maxWidth: 560, lineHeight: 1.65 }}>
              {content.heroSubhead}
            </p>
          </ScrollReveal>
        </section>
      </div>

      {/* CURRENCY TOGGLE */}
      <section style={{ padding: "0 clamp(20px, 4vw, 48px)", maxWidth: 1400, margin: "0 auto" }}>
        <ScrollReveal effect="fade-up">
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16, justifyContent: "space-between", marginBottom: 40 }}>
            <p style={{ fontSize: 13, color: MUTED, lineHeight: 1.6 }}>
              Prices shown in <span style={{ color: "#fff", fontWeight: 600 }}>{CURRENCIES[currency].label}</span> for {CURRENCIES[currency].region} — switch anytime.
            </p>
            <div role="group" aria-label="Choose currency" style={{ display: "inline-flex", gap: 4, padding: 4, borderRadius: 100, border: `1px solid ${BORDER}`, background: SURFACE }}>
              {(Object.keys(CURRENCIES) as Currency[]).map((c) => {
                const active = c === currency
                return (
                  <button
                    key={c}
                    onClick={() => chooseCurrency(c)}
                    aria-pressed={active}
                    style={{ position: "relative", padding: "8px 18px", borderRadius: 100, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600, background: "none", color: active ? "#fff" : MUTED }}
                  >
                    {active && (
                      <motion.span
                        layoutId="currencyPill"
                        style={{ position: "absolute", inset: 0, borderRadius: 100, background: ACCENT, zIndex: 0 }}
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                    <span style={{ position: "relative", zIndex: 1 }}>{CURRENCIES[c].flag} {CURRENCIES[c].label}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* BILLING TOGGLE */}
      <section style={{ padding: "0 clamp(20px, 4vw, 48px) 28px", maxWidth: 1400, margin: "0 auto" }}>
        <ScrollReveal effect="fade-up">
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div role="group" aria-label="Billing period" style={{ display: "inline-flex", gap: 4, padding: 4, borderRadius: 100, border: `1px solid ${BORDER}`, background: SURFACE }}>
              {(["monthly", "annual"] as const).map((b) => {
                const active = b === billing
                return (
                  <button
                    key={b}
                    onClick={() => setBilling(b)}
                    aria-pressed={active}
                    style={{ position: "relative", padding: "9px 22px", borderRadius: 100, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600, background: "none", color: active ? "#fff" : MUTED }}
                  >
                    {active && (
                      <motion.span
                        layoutId="billingPill"
                        style={{ position: "absolute", inset: 0, borderRadius: 100, background: ACCENT, zIndex: 0 }}
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                    <span style={{ position: "relative", zIndex: 1, textTransform: "capitalize" }}>{b === "annual" ? "Annual · 2 months free" : "Monthly"}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* PRICING CARDS */}
      <section style={{ padding: "0 clamp(20px, 4vw, 48px)", maxWidth: 1400, margin: "0 auto" }}>
        <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: 20, alignItems: "stretch" }}>
          {content.packages.map((p, i) => (
            <ScrollReveal key={p.number} effect="fade-up" delay={i * 0.08}>
              <motion.div
                whileHover={{ y: -6 }}
                transition={{ type: "spring", stiffness: 320, damping: 26 }}
                style={{
                  position: "relative",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  padding: "clamp(28px, 3vw, 40px)",
                  borderRadius: 24,
                  border: p.featured ? `1px solid rgba(196,84,26,0.45)` : `1px solid ${BORDER}`,
                  background: p.featured
                    ? `linear-gradient(160deg, rgba(196,84,26,0.12) 0%, ${SURFACE} 55%)`
                    : SURFACE,
                  boxShadow: p.featured ? "0 0 50px rgba(196,84,26,0.18)" : "none",
                }}
              >
                {p.badge && (
                  <span style={{ position: "absolute", top: 20, right: 20, padding: "5px 14px", borderRadius: 100, background: p.featured ? ACCENT : "transparent", border: p.featured ? "none" : `1px solid ${BORDER}`, color: p.featured ? "#fff" : MUTED, fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                    {p.badge}
                  </span>
                )}
                <p style={{ fontSize: 11, letterSpacing: "0.15em", color: MUTED, textTransform: "uppercase", marginBottom: 14 }}>{p.number}</p>
                <h2 style={{ fontSize: "clamp(22px, 2.2vw, 30px)", fontWeight: 900, letterSpacing: "-0.03em", marginBottom: 6 }}>{p.name}</h2>
                <p style={{ fontSize: 12, color: MUTED, marginBottom: 10, lineHeight: 1.4 }}>{p.forWho}</p>
                <p style={{ fontSize: 14, color: ACCENT, fontWeight: 600, marginBottom: 22 }}>{p.tagline}</p>

                <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 6 }}>
                  <span style={{ fontSize: "clamp(36px, 4vw, 52px)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 1 }}>
                    {fmtPrice(currency, billing === "annual" ? p.price[currency] * 10 : p.price[currency])}
                  </span>
                  <span style={{ fontSize: 16, color: MUTED, fontWeight: 600 }}>{billing === "annual" ? "/yr" : "/mo"}</span>
                </div>
                <p style={{ fontSize: 12, color: billing === "annual" ? ACCENT : "transparent", fontWeight: 700, marginBottom: 8, minHeight: 16 }}>
                  {billing === "annual" ? `2 months free · ~${fmtPrice(currency, Math.round(p.price[currency] * 10 / 12))}/mo` : "·"}
                </p>
                {!(p.openingSpecial && currency === p.openingSpecial.currency) && (
                  <div style={{ marginBottom: 18, padding: "9px 12px", borderRadius: 10, background: SURFACE, border: `1px solid ${BORDER}`, fontSize: 13, lineHeight: 1.4 }}>
                    {billing === "annual" ? (
                      <span style={{ fontWeight: 700, color: ACCENT }}>✓ Setup fee waived&nbsp;&nbsp;<span style={{ color: MUTED, fontWeight: 600 }}>· full year paid upfront</span></span>
                    ) : (
                      <span style={{ fontWeight: 700 }}>+ {fmtPrice(currency, p.setup[currency])} setup&nbsp;&nbsp;<span style={{ color: MUTED, fontWeight: 600 }}>· one-time, then billed monthly</span></span>
                    )}
                  </div>
                )}
                {p.openingSpecial && currency === p.openingSpecial.currency && (
                  <div style={{ marginBottom: 24, padding: "16px 18px", borderRadius: 16, border: "1px solid rgba(196,84,26,0.55)", background: "linear-gradient(160deg, rgba(196,84,26,0.16) 0%, rgba(196,84,26,0.06) 100%)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 12, fontWeight: 900, letterSpacing: "0.14em", textTransform: "uppercase", color: ACCENT }}>★ {p.openingSpecial.label}</span>
                      <span style={{ padding: "3px 10px", borderRadius: 100, background: ACCENT, color: "#fff", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>{p.openingSpecial.badge}</span>
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.25 }}>
                      {fmtPrice(currency, p.openingSpecial.upfront)} once-off <span style={{ color: MUTED, fontWeight: 700 }}>+</span> {fmtPrice(currency, p.openingSpecial.monthly)}/mo
                    </div>
                    <div style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>for {p.openingSpecial.months} months · <span style={{ textDecoration: "line-through" }}>{p.openingSpecial.regularNote}</span></div>
                  </div>
                )}
                {p.anchorNote && (
                  <p style={{ fontSize: 12.5, color: ACCENT, fontWeight: 600, lineHeight: 1.5, marginBottom: 14 }}>
                    {p.anchorNote[currency]}
                  </p>
                )}
                <p style={{ fontSize: 13, color: MUTED, lineHeight: 1.6, marginBottom: 28 }}>{p.description}</p>

                <ul style={{ listStyle: "none", padding: 0, display: "flex", flexDirection: "column", gap: 12, marginBottom: 32 }}>
                  {p.features.map((f) => (
                    <li key={f} style={{ display: "flex", gap: 12, alignItems: "flex-start", fontSize: 14, lineHeight: 1.5 }}>
                      <span style={{ color: ACCENT, marginTop: 1, flexShrink: 0 }}>✦</span>{f}
                    </li>
                  ))}
                </ul>

                <Link href={contactHref} style={{ marginTop: "auto" }}>
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    style={{
                      textAlign: "center",
                      padding: "16px 24px",
                      borderRadius: 100,
                      fontWeight: 700,
                      fontSize: 15,
                      cursor: "pointer",
                      background: p.featured ? ACCENT : "transparent",
                      color: "#fff",
                      border: p.featured ? "none" : `1px solid ${BORDER}`,
                    }}
                  >
                    Book a Call
                  </motion.div>
                </Link>
                <Link href="/estimate" style={{ display: "block", textAlign: "center", marginTop: 12, fontSize: 13, color: ACCENT, fontWeight: 600 }}>
                  or get an instant estimate →
                </Link>
              </motion.div>
            </ScrollReveal>
          ))}
        </div>
        {/* INSTANT ESTIMATE — the no-call path to a number */}
        <ScrollReveal effect="fade-up">
          <Link href="/estimate" style={{ display: "block" }}>
            <motion.div
              whileHover={{ scale: 1.01, y: -2 }}
              transition={{ type: "spring", stiffness: 320, damping: 26 }}
              style={{
                marginTop: 28,
                padding: "clamp(22px, 3vw, 32px)",
                borderRadius: 20,
                border: "1px solid rgba(196,84,26,0.5)",
                background: "linear-gradient(135deg, rgba(196,84,26,0.14) 0%, rgba(255,255,255,0.04) 60%)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 18,
                flexWrap: "wrap",
                cursor: "pointer",
              }}
            >
              <div style={{ maxWidth: 640 }}>
                <p style={{ fontWeight: 900, fontSize: "clamp(17px, 1.8vw, 21px)", letterSpacing: "-0.02em", marginBottom: 6 }}>
                  ⚡ Ready for your number? Get an instant estimate
                </p>
                <p style={{ color: MUTED, fontSize: 13.5, lineHeight: 1.55 }}>
                  Pick a package — or chat and let our AI pick it for you — and a formal estimate lands in your inbox in about 60 seconds. No call needed.
                </p>
              </div>
              <span style={{ padding: "13px 28px", background: ACCENT, color: "#fff", borderRadius: 100, fontWeight: 700, fontSize: 14, whiteSpace: "nowrap" }}>
                Get my estimate →
              </span>
            </motion.div>
          </Link>
        </ScrollReveal>

        <ScrollReveal effect="fade-up">
          <div style={{ textAlign: "center", marginTop: 28 }}>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", lineHeight: 1.6, fontWeight: 600 }}>
              No minimum term: cancel any time. <strong style={{ color: "rgba(255,255,255,0.85)" }}>Monthly</strong> — billed each month plus a one-time setup fee. <strong style={{ color: "rgba(255,255,255,0.85)" }}>Annual</strong> — full year paid upfront, 2 months free, and setup fee waived.
            </p>
            <p style={{ fontSize: 13, color: MUTED, marginTop: 10, lineHeight: 1.6 }}>
              Not sure which fits? <Link href={contactHref} style={{ color: ACCENT, fontWeight: 600 }}>Book a free call</Link> and we&rsquo;ll point you to the right one — no hard sell.
            </p>
            <p style={{ fontSize: 13, color: MUTED, marginTop: 14, lineHeight: 1.6 }}>
              <a href={`/boldpiq-offers-${currency.toLowerCase()}.pdf`} download style={{ color: ACCENT, fontWeight: 600 }}>Download the {currency} pricing — monthly &amp; annual (PDF) &darr;</a>
            </p>
          </div>
        </ScrollReveal>
      </section>

      {/* CUSTOM / HIGH ANCHOR */}
      <section style={{ padding: "clamp(44px, 6vw, 72px) clamp(20px, 4vw, 48px) 0", maxWidth: 1400, margin: "0 auto" }}>
        <ScrollReveal effect="fade-up">
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 24, padding: "clamp(28px, 3vw, 44px)", borderRadius: 24, border: `1px solid ${BORDER}`, background: `linear-gradient(135deg, rgba(196,84,26,0.06) 0%, ${SURFACE} 60%)` }}>
            <div style={{ maxWidth: 620 }}>
              <p style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: MUTED, marginBottom: 12 }}>Custom</p>
              <h3 style={{ fontSize: "clamp(22px, 2.4vw, 34px)", fontWeight: 900, letterSpacing: "-0.03em", marginBottom: 12, lineHeight: 1.05 }}>{content.custom.heading}</h3>
              <p style={{ fontSize: 15, color: MUTED, lineHeight: 1.65 }}>{content.custom.body}</p>
            </div>
            <Link href={contactHref}>
              <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} style={{ padding: "16px 36px", border: `1px solid ${ACCENT}`, color: "#fff", borderRadius: 100, fontWeight: 700, fontSize: 15, cursor: "pointer", whiteSpace: "nowrap" }}>
                Let&rsquo;s talk
              </motion.div>
            </Link>
          </div>
        </ScrollReveal>
      </section>

      {/* SOCIAL PROOF */}
      <section style={{ padding: "clamp(48px, 7vw, 88px) clamp(20px, 4vw, 48px) 0", maxWidth: 1100, margin: "0 auto" }}>
        <ScrollReveal effect="fade-up">
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <Eyebrow label="Proof" />
          </div>
        </ScrollReveal>
        <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 20 }}>
          {[
            { quote: "Her commitment to quality and her ability to capture the spirit of our brand in every aspect of the site… highly recommend her services.", cite: "The Cherri Chilli", href: "/work/case-the-cherri-chilli" },
            { quote: "We are very fortunate to have Monique develop a website and would recommend her to anyone.", cite: "Net Vir Pret", href: "/work/case-netvirpret" },
          ].map((t, i) => (
            <ScrollReveal key={t.cite} effect="fade-up" delay={i * 0.08}>
              <Link href={t.href} style={{ display: "block", height: "100%" }}>
                <motion.div whileHover={{ y: -4 }} transition={{ type: "spring", stiffness: 320, damping: 26 }} style={{ height: "100%", padding: "clamp(24px, 3vw, 36px)", borderRadius: 20, border: `1px solid ${BORDER}`, background: SURFACE }}>
                  <blockquote style={{ borderLeft: `3px solid ${ACCENT}`, paddingLeft: 20, margin: 0 }}>
                    <p style={{ fontSize: "clamp(15px, 1.4vw, 18px)", fontStyle: "italic", lineHeight: 1.6, color: "#fff", marginBottom: 14 }}>&ldquo;{t.quote}&rdquo;</p>
                    <cite style={{ fontSize: 13, color: MUTED, fontStyle: "normal" }}>— {t.cite}</cite>
                  </blockquote>
                </motion.div>
              </Link>
            </ScrollReveal>
          ))}
        </div>
        <ScrollReveal effect="fade-up">
          <p style={{ textAlign: "center", fontSize: 13, color: MUTED, marginTop: 24 }}>
            <Link href="/work" style={{ color: ACCENT, fontWeight: 600 }}>See more of our work →</Link>
          </p>
        </ScrollReveal>
      </section>

      {/* FAQ */}
      <section style={{ padding: "clamp(60px, 8vw, 110px) clamp(20px, 4vw, 48px) clamp(60px, 8vw, 100px)", maxWidth: 800, margin: "0 auto" }}>
        <ScrollReveal effect="fade-up">
          <Eyebrow label="FAQ" />
          <h2 style={{ fontSize: "clamp(28px, 3vw, 48px)", fontWeight: 900, letterSpacing: "-0.04em", marginBottom: 48 }}>
            Common<br /><span style={{ color: ACCENT }}>questions.</span>
          </h2>
        </ScrollReveal>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {content.faqs.map((faq, i) => (
            <ScrollReveal key={i} effect="fade-up" delay={i * 0.05}>
              <div style={{ borderBottom: `1px solid ${BORDER}` }}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{ width: "100%", textAlign: "left", padding: "24px 0", background: "none", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, fontFamily: "inherit" }}
                >
                  <span style={{ fontSize: "clamp(16px, 1.2vw, 18px)", fontWeight: 600, color: "#fff" }}>{faq.q}</span>
                  <motion.span animate={{ rotate: openFaq === i ? 45 : 0 }} style={{ fontSize: 24, color: ACCENT, flexShrink: 0, display: "block", lineHeight: 1 }}>+</motion.span>
                </button>
                <motion.div initial={false} animate={{ height: openFaq === i ? "auto" : 0, opacity: openFaq === i ? 1 : 0 }} style={{ overflow: "hidden" }}>
                  <p style={{ color: MUTED, fontSize: 16, lineHeight: 1.7, paddingBottom: 24 }}>{faq.a}</p>
                </motion.div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <div style={{ borderTop: `1px solid ${BORDER}`, padding: "clamp(60px, 8vw, 100px) clamp(20px, 4vw, 48px)" }}>
        <ScrollReveal effect="scale">
          <div style={{ maxWidth: 1400, margin: "0 auto", textAlign: "center" }}>
            <h2 style={{ fontSize: "clamp(36px, 5vw, 80px)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 0.92, marginBottom: 32 }}>
              {content.cta.title[0]}<br /><span style={{ color: ACCENT }}>{content.cta.title[1]}</span>
            </h2>
            <p style={{ color: MUTED, fontSize: 18, maxWidth: 480, margin: "0 auto 48px", lineHeight: 1.65 }}>
              {content.cta.subhead}
            </p>
            <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
              <Link href={contactHref}>
                <motion.div whileHover={{ scale: 1.04, background: "#D4601F" }} whileTap={{ scale: 0.97 }} style={{ padding: "18px 48px", background: ACCENT, color: "#fff", borderRadius: 100, fontWeight: 700, fontSize: 16, cursor: "pointer" }}>
                  {content.cta.primary}
                </motion.div>
              </Link>
              <Link href="/work">
                <motion.div whileHover={{ borderColor: "rgba(255,255,255,0.3)" }} style={{ padding: "18px 48px", border: `1px solid ${BORDER}`, color: "#fff", borderRadius: 100, fontWeight: 600, fontSize: 16, cursor: "pointer" }}>
                  See Our Work
                </motion.div>
              </Link>
            </div>
            <p style={{ marginTop: 28, fontSize: 13, color: MUTED }}>
              Full refund before kickoff — cancel before we start building and you don&rsquo;t pay.
            </p>
          </div>
        </ScrollReveal>
      </div>

      <Footer />
    </main>
  )
}
